<template>
  <a-modal width="600px" title="Reason for Denying Violation" @cancel="closeModal" :visible="visible"
    :destroy-on-close="true" data-testid="project-shift-auths-deny-modal">
    <div class="flex flex-column gap-3" data-testid="project-shift-auths-deny-content">
      <a-textarea rows="5" v-model="ownReason" placeholder="Description"
        data-testid="project-shift-auths-deny-reason-input" />

      <div v-if="disciplinarySettings" data-testid="project-shift-auths-deny-disciplinary-settings">
        <b>Rules of the Automatic Disciplinary Action System</b>
        <p>
          The system automatically applies disciplinary actions based on the number of declined violations by an
          associate. Below are the steps taken for each violation:
        </p>
        <b>Payroll Violations:</b>
        <ul>
          <li v-for="item in getInfo('payroll')" :key="item">{{ item }}</li>
        </ul>
        <b>Disciplinary Violations:</b>
        <ul>
          <li v-for="item in getInfo('disciplinary')" :key="item">
            {{ item }}
          </li>
        </ul>
      </div>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between" data-testid="project-shift-auths-deny-footer">
        <a-button @click="closeModal" data-testid="project-shift-auths-deny-cancel-button"> Cancel </a-button>
        <div class="flex justify-content-end">
          <a-button type="danger" @click="confirm(false)" data-testid="project-shift-auths-deny-confirm-button">
            Deny
          </a-button>
          <a-button v-if="isAdjusted" type="danger" @click="confirm(true)"
            data-testid="project-shift-auths-deny-adjust-button">
            Deny & Adjust
          </a-button>
        </div>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Input, Button, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import AuthReasonMixin from '../mixins/AuthReasonMixin'

const { mapActions: documentActions } = createNamespacedHelpers('document')
const { mapActions: projectActions, mapState: projectState } = createNamespacedHelpers('project')

export default {
  components: {
    'a-textarea': Input.TextArea,
    'a-button': Button
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    isAdjusted: {
      type: Boolean,
      default: false
    },
    reason: {
      type: String,
      default: ''
    },
    shift: {
      type: Object,
      default: undefined
    },
    authorizations: {
      type: Array,
      default: () => []
    }
  },
  mixins: [AuthReasonMixin],
  data() {
    return {
      selectedDeclineReason: undefined,
      ownReason: ''
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.selectedDeclineReason = this.authorizations[0].type

          this.ownReason = this.selectedDeclineReason ? this.getFullReason(this.selectedDeclineReason, this.shift) : ''

          this.loadData()
        }
      },
      immediate: true
    },
    ownReason: {
      handler(newValue) {
        this.$emit('update:reason', newValue.replace(/\n+/g, ' ').trim())
      }
    }
  },
  computed: {
    ...projectState({
      disciplinarySettings: state => state.disciplinarySettings
    })
  },
  methods: {
    ...projectActions(['getProjectDisciplinaryActions']),
    ...documentActions(['getDocumentByType']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    getInfo(key) {
      return this.disciplinarySettings[key].map(item => {
        const getOrdinalSuffix = n => {
          if (n % 100 >= 11 && n % 100 <= 13) return 'th'
          switch (n % 10) {
            case 1:
              return 'st'
            case 2:
              return 'nd'
            case 3:
              return 'rd'
            default:
              return 'th'
          }
        }

        const step = `${item.step_number}${getOrdinalSuffix(item.step_number)} Violation`

        const actions = []

        if (item.write_up) actions.push('Written Warning')
        if (item.penalty) actions.push('Suspension')
        if (item.suspension) actions.push('Block')
        if (item.notification) {
          actions.push(
            `Notification to ${item.notification_scope.map(scope =>
              scope
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            )}`
          )
        }
        return actions.length > 0 ? `${step} → ${actions.join(' and ')}` : `${step} → No actions`
      })
    },

    closeModal() {
      this.ownReason = ''
      this.$emit('update:visible', false)
      this.$emit('update:reason', '')
    },

    confirm(checked) {
      if (!this.ownReason) {
        notification.warning({
          message: 'Error',
          description: 'Please fill deny reason and try again'
        })
      } else {
        this.$emit('update:visible', false)
        this.$emit('confirmDeny', checked)
      }
    },

    async loadData() {
      try {
        await this.getProjectDisciplinaryActions()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    }
  }
}
</script>

<style>
.custom-dropdown-option {
  white-space: normal;
  word-wrap: break-word;
  min-height: 20px;
  line-height: 1.5;
  padding: 5px 12px;
}
</style>
